import { PreviewWithId } from 'Components'
import { getApiUrl } from 'Helpers'
import axios from 'axios'

// Types for job response
export interface SupportRequestResponse {
  jobId: number
}

export const supportRequestBasic = async (
  message: string,
  orgId: string,
  orgName: string,
  requesterName: string,
  requesterEmail: string,
  orgUrl: string,
  files?: PreviewWithId[],
  copyTo?: string[],
  replyTo?: string[],
): Promise<SupportRequestResponse> => {
  const url = `${getApiUrl()}/v1`
  const token = localStorage.getItem('eltoro_token')
  const config = {
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
  }

  // Convert files to base64 attachments
  const attachments = files
    ? await Promise.all(
        files.map(async (file) => {
          return new Promise<{ filename: string; chunk: string }>(
            (resolve, reject) => {
              const reader = new FileReader()
              reader.onload = () => {
                const [, base64] = (reader.result as string).split(',') // Remove data URL prefix
                resolve({
                  filename: file.file.name,
                  chunk: base64,
                })
              }
              reader.onerror = reject
              reader.readAsDataURL(file.file)
            },
          )
        }),
      )
    : []

  const requestBody = {
    route_identifier: 'SupportRequestBasic',
    org_id: orgId,
    template_variables: {
      requesterName: {
        single_value: requesterName,
      },
      requesterEmail: {
        single_value: requesterEmail,
      },
      orgName: {
        single_value: orgName,
      },
      orgUrl: {
        single_value: orgUrl,
      },
      message: {
        single_value: message,
      },
    },
    includes: {
      cc: [...(copyTo || []), requesterEmail],
      reply_to: replyTo,
    },
    attachments,
  }

  return await axios
    .post(
      `${url}/notifications/routes/SupportRequestBasic/trigger`,
      requestBody,
      {
        headers: config,
      },
    )
    .then((res) => {
      return res.data
    })
    .catch((err) => {
      if (err) {
        throw new Error(
          JSON.stringify({
            status: err?.response?.status,
            message: err?.response?.data,
          }),
        )
      }
    })
}

export const supportRequestOrderLine = async (
  message: string,
  orgId: string,
  orgName: string,
  requesterName: string,
  requesterEmail: string,
  orgUrl: string,
  orderLineUrl: string,
  orderLineName: string,
  adminPortalLink: string,
  orderLineId: string,
  campaignUrl: string,
  campaignName: string,
  orderLineStatus: string,
  orderLineStart: string,
  orderLineStop: string,
  orderLineImpressions: string,
  files?: PreviewWithId[],
  copyTo?: string[],
  replyTo?: string[],
): Promise<SupportRequestResponse> => {
  const url = `${getApiUrl()}/v1`
  const token = localStorage.getItem('eltoro_token')
  const config = {
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
  }

  // Convert files to base64 attachments
  const attachments = files
    ? await Promise.all(
        files.map(async (file) => {
          return new Promise<{ filename: string; chunk: string }>(
            (resolve, reject) => {
              const reader = new FileReader()
              reader.onload = () => {
                const [, base64] = (reader.result as string).split(',') // Remove data URL prefix
                resolve({
                  filename: file.file.name,
                  chunk: base64,
                })
              }
              reader.onerror = reject
              reader.readAsDataURL(file.file)
            },
          )
        }),
      )
    : []

  const requestBody = {
    route_identifier: 'SupportRequestOrderLine',
    org_id: orgId,
    template_variables: {
      requesterName: {
        single_value: requesterName,
      },
      requesterEmail: {
        single_value: requesterEmail,
      },
      orgName: {
        single_value: orgName,
      },
      orgUrl: {
        single_value: orgUrl,
      },
      message: {
        single_value: message,
      },
      orderLineUrl: {
        single_value: orderLineUrl,
      },
      orderLineName: {
        single_value: orderLineName,
      },
      adminPortalLink: {
        single_value: adminPortalLink,
      },
      orderLineId: {
        single_value: orderLineId,
      },
      campaignUrl: {
        single_value: campaignUrl,
      },
      campaignName: {
        single_value: campaignName,
      },
      orderLineStatus: {
        single_value: orderLineStatus,
      },
      orderLineStart: {
        single_value: orderLineStart,
      },
      orderLineStop: {
        single_value: orderLineStop,
      },
      orderLineImpressions: {
        single_value: orderLineImpressions,
      },
    },
    includes: {
      cc: [...(copyTo || []), requesterEmail],
      reply_to: replyTo,
    },
    attachments,
  }

  return await axios
    .post(
      `${url}/notifications/routes/SupportRequestOrderLine/trigger`,
      requestBody,
      {
        headers: config,
      },
    )
    .then((res) => {
      return res.data
    })
    .catch((err) => {
      if (err) {
        throw new Error(
          JSON.stringify({
            status: err?.response?.status,
            message: err?.response?.data,
          }),
        )
      }
    })
}

export const supportRequestCampaign = async (
  customerNotes: string,
  orgId: string,
  orgName: string,
  requesterName: string,
  requesterEmail: string,
  orgUrl: string,
  campaignUrl: string,
  campaignName: string,
  campaignId: string,
  campaignStart: string,
  campaignStop: string,
  adminPortalLink: string,
  files?: PreviewWithId[],
  copyTo?: string[],
  replyTo?: string[],
): Promise<SupportRequestResponse> => {
  const url = `${getApiUrl()}/v1`
  const token = localStorage.getItem('eltoro_token')
  const config = {
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
  }

  // Convert files to base64 attachments
  const attachments = files
    ? await Promise.all(
        files.map(async (file) => {
          return new Promise<{ filename: string; chunk: string }>(
            (resolve, reject) => {
              const reader = new FileReader()
              reader.onload = () => {
                const [, base64] = (reader.result as string).split(',') // Remove data URL prefix
                resolve({
                  filename: file.file.name,
                  chunk: base64,
                })
              }
              reader.onerror = reject
              reader.readAsDataURL(file.file)
            },
          )
        }),
      )
    : []

  const requestBody = {
    route_identifier: 'SupportRequestCampaign',
    org_id: orgId,
    template_variables: {
      requesterName: {
        single_value: requesterName,
      },
      requesterEmail: {
        single_value: requesterEmail,
      },
      orgName: {
        single_value: orgName,
      },
      orgUrl: {
        single_value: orgUrl,
      },
      customerNotes: {
        single_value: customerNotes,
      },
      campaignUrl: {
        single_value: campaignUrl,
      },
      campaignName: {
        single_value: campaignName,
      },
      campaignId: {
        single_value: campaignId,
      },
      campaignStart: {
        single_value: campaignStart,
      },
      campaignStop: {
        single_value: campaignStop,
      },
      adminPortalLink: {
        single_value: adminPortalLink,
      },
    },
    includes: {
      cc: [...(copyTo || []), requesterEmail],
      reply_to: replyTo,
    },
    attachments,
  }

  return await axios
    .post(
      `${url}/notifications/routes/SupportRequestCampaign/trigger`,
      requestBody,
      {
        headers: config,
      },
    )
    .then((res) => {
      return res.data
    })
    .catch((err) => {
      if (err) {
        throw new Error(
          JSON.stringify({
            status: err?.response?.status,
            message: err?.response?.data,
          }),
        )
      }
    })
}
